import { useState } from "react"
import { DateRangePicker } from "../../shared/dateRangePicker"
import { SearchSelect } from "../../shared/searchSelect"
import { SearchFiltersModal } from "../../shared/searchFiltersModal"
import stringHelpers from "../../../utilities/stringHelpers"
import { toDateString } from "../../../utilities/dateUtilities"
import { reportDatePresetByLabel } from "../../reports/reportsEnums"
import { TagSelector } from "../../shared/tagSelector"
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities"
import arrayUtilities from "../../../utilities/arrayUtilities"

const DEFAULT_DATE_SEARCH_MODE = 1

export const PaymentsDataFiltersModal = ({
    showModal,
    handleClose,
    handleConfirm,
    initialFromDate,
    initialToDate,
    initialDatePreset,
    initialDateSearchMode,
    initialSiteId,
    initialSelectedTags = [],
    initialAppliedFilters,
    sites = [],
    tags = []
}) => {
    const [fromDate, setFromDate] = useState(initialFromDate)
    const [toDate, setToDate] = useState(initialToDate)
    const [datePreset, setDatePreset] = useState(initialDatePreset)
    const [dateSearchMode, setDateSearchMode] = useState(initialDateSearchMode)
    const [siteId, setSiteId] = useState(initialSiteId)
    const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

    const clearLocalFilters = () => {
        setFromDate(null)
        setToDate(null)
        setDatePreset(null)
        setDateSearchMode(DEFAULT_DATE_SEARCH_MODE)
        setSiteId(null)
        setSelectedTags([])
        setAppliedFilters({
            "Date Mode": "Allow Overlap",
        })
    }

    const handleTagsChange = tagSelections => {
        setSelectedTags(tagSelections)
        setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
    }

    return <SearchFiltersModal
        title="Filter Results"
        handleClose={ handleClose }
        handleConfirm={ () => handleConfirm({ fromDate, toDate, datePreset, dateSearchMode, siteId, selectedTags, appliedFilters }) }
        showModal={ showModal }
        key="payments-data-filters-modal"
        handleReset={ clearLocalFilters }
    >
        <div className="w-100">
            <div className="mb-2">
                <label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
                <DateRangePicker
                    fromDate={ fromDate }
                    toDate={ toDate }
                    setDateRange={ (firstDate, lastDate, presetLabel) => {
                        const isFirstDateEmpty = firstDate === null || firstDate === ""
                        const isLastDateEmpty = lastDate === null || lastDate === ""
                        setFromDate(isFirstDateEmpty ? null : firstDate);
                        setToDate(isLastDateEmpty ? null : lastDate);
                        setDatePreset(stringHelpers.isNullOrEmpty(presetLabel) ? null : (reportDatePresetByLabel[presetLabel] ?? null))
                        if (!stringHelpers.isNullOrEmpty(presetLabel)) {
                            setAppliedFilters(prevState => {
                                const updatedFilters = { ...prevState }
                                delete updatedFilters.From
                                delete updatedFilters.To
                                updatedFilters["Date Range"] = presetLabel
                                return updatedFilters
                            })
                        }
                        else {
                            setAppliedFilters(prevState => {
                                const updatedFilters = { ...prevState }
                                if (!isFirstDateEmpty && !isLastDateEmpty) {
                                    delete updatedFilters.From
                                    delete updatedFilters.To
                                    updatedFilters["Date Range"] = `${ toDateString(new Date(firstDate)) } - ${ toDateString(new Date(lastDate)) }`
                                } else {
                                    if (!isFirstDateEmpty) {
                                        updatedFilters.From = toDateString(new Date(firstDate))
                                    } else {
                                        delete updatedFilters.From
                                    }
                                    if (!isLastDateEmpty) {
                                        updatedFilters.To = toDateString(new Date(lastDate))
                                    } else {
                                        delete updatedFilters.To
                                    }
                                    delete updatedFilters["Date Range"]
                                }
                                return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                            })
                        }
                    } }
                    showClearOption
                    hideLabel
                    fullWidth
                    dropdownPositionRight
                />
            </div>

            <div className="d-flex flex-column mb-2">
                <label className="m-0 mr-2 mb-1 nowrap">Date Mode</label>
                <select
                    id="date-search-mode-selector"
                    value={ dateSearchMode === null ? "" : dateSearchMode }
                    onChange={ e => {
                        const value = Number(e.target.value)
                        setDateSearchMode(value)
                        setAppliedFilters(prevState => ({
                            ...prevState,
                            "Date Mode": value === 0 ? "Strict" : "Allow Overlap"
                        }))
                    } }
                    className="border rounded px-2 py-1 w-100"
                >
                    <option value="0">Strict</option>
                    <option value="1">Allow Overlap</option>
                </select>
            </div>

            <div className="d-flex flex-column mb-2">
                <label className="m-0 mr-2 mb-1 nowrap">Site</label>
                <SearchSelect
                    defaultLabel="All"
                    width="100%"
                    options={ [{ value: "", label: "All" }, ...sites.map(item => ({ value: item.id, label: item.name }))] }
                    onSelect={ value => {
                        if (!stringHelpers.isNullOrEmpty(value)) {
                            setSiteId(value)
                            const selectedSite = sites.find(item => item.id === value)
                            setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSite.name }))
                        } else {
                            setSiteId(null)
                            setAppliedFilters(prevState => {
                                const updatedFilters = { ...prevState }
                                delete updatedFilters["Site"]
                                return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                            })
                        }
                    } }
                    selected={ !stringHelpers.isNullOrEmpty(siteId) ? siteId : null }
                />
            </div>

            { !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
                <div className="d-flex align-items-center justify-content-between">
                    <label className="m-0 mr-2 mb-1 nowrap">Tags</label>
                    <button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
                </div>
                <TagSelector
                    onChange={ handleTagsChange }
                    selectedTags={ selectedTags }
                    tags={ tags }
                />
            </div> }
        </div>
    </SearchFiltersModal>
}
